import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Bug, RefreshCw, Sparkles, AlertTriangle, Lightbulb } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import CodeBlock from "./CodeBlock";

const DANGER_TONE = {
  low: "bg-teal/15 text-teal",
  medium: "bg-saffron/15 text-marigold",
  high: "bg-red-500/15 text-red-600",
};

export default function GameDebugDialog({ open, onOpenChange, defaultEngine = "SpriteKit" }) {
  const [errText, setErrText] = useState("");
  const [code, setCode] = useState("");
  const [engine, setEngine] = useState(defaultEngine);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) { setErrText(""); setCode(""); setResult(null); }
  }, [open]);

  const debug = async () => {
    if (errText.trim().length < 5) return toast.error("Paste the actual error message");
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/games/debug", {
        error: errText.trim(),
        code: code.trim(),
        engine,
      });
      setResult(data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) toast.error(err.response?.data?.detail || "Quota cap hit. Upgrade for unlimited debug help.");
      else toast.error(err.response?.data?.detail || "Could not debug. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream border-2 border-ink rounded-3xl max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="game-debug-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
            <Bug className="w-5 h-5 text-marigold" /> Debug Assistant
          </DialogTitle>
          <DialogDescription className="text-ink2">
            Paste a Swift / SpriteKit / SceneKit / RealityKit error and (optionally) the offending code.
            Get a plain-English diagnosis, a drop-in fix, and the why behind it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-4">
          <div>
            <div className="label-eyebrow mb-2">Engine</div>
            <div className="inline-flex rounded-full border-2 border-ink p-1 text-xs font-bold uppercase tracking-wider">
              {["SpriteKit", "SceneKit", "RealityKit"].map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setEngine(e)}
                  className={`px-3 py-1.5 rounded-full transition ${engine === e ? "bg-ink text-cream" : "text-ink2 hover:text-ink"}`}
                  data-testid={`debug-engine-${e}`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="label-eyebrow mb-2">Error message *</div>
            <textarea
              value={errText}
              onChange={(e) => setErrText(e.target.value)}
              rows={4}
              maxLength={8000}
              placeholder="error: cannot convert value of type 'CGPoint' to expected argument type 'CGVector'"
              className="w-full card-soft p-3 text-sm font-mono bg-transparent outline-none resize-none"
              data-testid="debug-error-input"
            />
          </div>

          <div>
            <div className="label-eyebrow mb-2">Code (optional, helps a lot)</div>
            <textarea
              value={code}
              onChange={(e) => setCode(e.target.value)}
              rows={6}
              maxLength={8000}
              placeholder={`override func touchesBegan(_ touches: Set<UITouch>, with event: UIEvent?) {\n    player.physicsBody?.applyImpulse(CGPoint(x: 0, y: 140))\n}`}
              className="w-full card-soft p-3 text-xs font-mono bg-transparent outline-none resize-none"
              data-testid="debug-code-input"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button
              onClick={() => onOpenChange(false)}
              className="text-sm px-4 py-2 rounded-full border border-border text-ink2 hover:border-ink hover:text-ink"
              data-testid="debug-close"
            >
              Close
            </button>
            <button
              onClick={debug}
              disabled={busy}
              className="btn-primary text-sm py-2 disabled:opacity-40"
              data-testid="debug-submit"
            >
              {busy ? <><RefreshCw className="w-4 h-4 animate-spin" /> Diagnosing…</> : <><Sparkles className="w-4 h-4" /> Diagnose & fix</>}
            </button>
          </div>
        </div>

        {result && (
          <div className="mt-5 space-y-4" data-testid="debug-result">
            <div className="card-soft p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <AlertTriangle className="w-4 h-4 text-marigold" />
                <span className="label-eyebrow">Diagnosis</span>
                {result.danger_level && (
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${DANGER_TONE[result.danger_level] || "bg-paper"}`}>
                    {result.danger_level} severity
                  </span>
                )}
              </div>
              <p className="text-sm leading-relaxed">{result.diagnosis}</p>
              {result.root_cause && (
                <p className="text-sm text-ink2 mt-2"><span className="font-semibold text-ink">Root cause:</span> {result.root_cause}</p>
              )}
            </div>

            {result.fix && (
              <div>
                <div className="label-eyebrow mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-marigold" /> Drop-in fix
                </div>
                <CodeBlock code={result.fix} filename="fix.swift" />
              </div>
            )}

            {result.explanation && (
              <div className="card-soft p-4">
                <div className="label-eyebrow mb-1.5">Why this works</div>
                <p className="text-sm text-ink2 leading-relaxed">{result.explanation}</p>
              </div>
            )}

            {(result.next_steps || []).length > 0 && (
              <div className="card-soft p-4">
                <div className="label-eyebrow mb-2 flex items-center gap-1.5">
                  <Lightbulb className="w-3.5 h-3.5 text-marigold" /> Next steps
                </div>
                <ul className="space-y-1.5 text-sm text-ink2">
                  {result.next_steps.map((s, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-marigold/15 text-marigold font-mono font-bold text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
